'use client';

import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslations } from 'next-intl';
import { AlertTriangle } from 'lucide-react';
import { resolveApiErrorToastMessageOrFallback } from '../lib/api/localizeApiError';
import { PushNotificationType, type NotificationPreference, type PushSubscriptionPayload } from '../lib/api';
import {
  useSubscribePushNotificationMutation,
  useUnsubscribePushNotificationMutation,
} from '../hooks/queries/useMember';
import {
  useNotificationPreferences,
  useSendTestNotificationMutation,
  useUpdateNotificationPreferencesMutation,
} from '../hooks/queries/usePushNotifications';
import { isPortalBuild } from '../lib/appTarget';
import { resolveAppHref } from '../lib/portalRoutes';
import { getAuthSession } from '../lib/authSession';
import { getDesktopPushEndpoint, hasDesktopBridge } from '../lib/desktopBridge';
import { queryKeys } from '../hooks/queries/queryKeys';
import { CoActionsIcon, ConventionsIcon, DaemonTriggersIcon, DocumentsIcon, PlansIcon } from './layout/Sidebar/icons';
import { Spinner } from './loading/Spinner';

const vapidPublicKey = process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY ?? '';

const base64UrlToUint8Array = (value: string) => {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
  const raw = atob(padded);
  const output = new Uint8Array(raw.length);

  for (let index = 0; index < raw.length; index += 1) {
    output[index] = raw.charCodeAt(index);
  }

  return output;
};

const isIosDevice = () => {
  if (typeof navigator === 'undefined') {
    return false;
  }

  return /iPad|iPhone|iPod/.test(navigator.userAgent);
};

const isAndroidDevice = () => {
  if (typeof navigator === 'undefined') {
    return false;
  }

  return /Android/.test(navigator.userAgent);
};

type NotificationTypeKey = NotificationPreference['type'];

type NotificationGroup = {
  id: 'convention' | 'document' | 'plan' | 'activity' | 'runner';
  labelKey: string;
  icon: (props: { className?: string }) => React.ReactElement;
  types: ReadonlyArray<{ type: NotificationTypeKey; labelKey: string }>;
};

const notificationGroups: ReadonlyArray<NotificationGroup> = [
  {
    id: 'convention',
    labelKey: 'notificationGroupConvention',
    icon: ConventionsIcon,
    types: [
      { type: PushNotificationType.CONVENTION_CREATED, labelKey: 'notificationTypeConventionCreated' },
      { type: PushNotificationType.CONVENTION_UPDATED, labelKey: 'notificationTypeConventionUpdated' },
      { type: PushNotificationType.CONVENTION_DELETED, labelKey: 'notificationTypeConventionDeleted' },
    ],
  },
  {
    id: 'document',
    labelKey: 'notificationGroupDocument',
    icon: DocumentsIcon,
    types: [
      { type: PushNotificationType.DOCUMENT_CREATED, labelKey: 'notificationTypeDocumentCreated' },
      { type: PushNotificationType.DOCUMENT_UPDATED, labelKey: 'notificationTypeDocumentUpdated' },
      { type: PushNotificationType.DOCUMENT_DELETED, labelKey: 'notificationTypeDocumentDeleted' },
      { type: PushNotificationType.DOCUMENT_COMMENT, labelKey: 'notificationTypeDocumentComment' },
    ],
  },
  {
    id: 'plan',
    labelKey: 'notificationGroupPlan',
    icon: PlansIcon,
    types: [
      { type: PushNotificationType.PLAN_CREATED, labelKey: 'notificationTypePlanCreated' },
      { type: PushNotificationType.PLAN_STATUS_CHANGED, labelKey: 'notificationTypePlanStatusChanged' },
    ],
  },
  {
    id: 'activity',
    labelKey: 'notificationGroupActivity',
    icon: CoActionsIcon,
    types: [
      { type: PushNotificationType.CO_ACTION_CREATED, labelKey: 'notificationTypeCoActionCreated' },
      { type: PushNotificationType.COMMENT, labelKey: 'notificationTypeComment' },
      { type: PushNotificationType.COMPLETION_REPORT, labelKey: 'notificationTypeCompletionReport' },
      { type: PushNotificationType.POST_MORTEM, labelKey: 'notificationTypePostMortem' },
      { type: PushNotificationType.CODE_REVIEW_COMPLETED, labelKey: 'notificationTypeCodeReviewCompleted' },
    ],
  },
  {
    id: 'runner',
    labelKey: 'notificationGroupRunner',
    icon: DaemonTriggersIcon,
    types: [
      {
        type: PushNotificationType.DAEMON_TRIGGER_STATUS_CHANGED,
        labelKey: 'notificationTypeDaemonTriggerStatusChanged',
      },
      { type: PushNotificationType.RUNNER_UPDATE_COMPLETED, labelKey: 'notificationTypeRunnerUpdateCompleted' },
    ],
  },
];

const allNotificationTypes = notificationGroups.flatMap((g) => g.types);

const isDefaultNotificationEnabled = (type: NotificationTypeKey) =>
  type !== PushNotificationType.DAEMON_TRIGGER_STATUS_CHANGED;

// 알림을 다시 켤 때 기본 preference로 복원해야 하는지 판단한다.
// - preference row가 전혀 없으면(신규) 기본값으로 초기화한다.
// - 전체 끄기(unsubscribe)로 모든 타입이 false로 남은 상태라면 기본값으로 복원한다.
// - 일부만 켜져 있으면(사용자가 의도적으로 일부만 끈 경우) 그대로 둔다.
export const shouldRestoreDefaultPreferences = (existing: NotificationPreference[] | undefined): boolean => {
  if (!Array.isArray(existing) || existing.length === 0) {
    return true;
  }
  return existing.every((item) => !item.enabled);
};

type NotificationSettingsViewProps = {
  projectId: string;
  isSubscribed: boolean;
  pushEndpoint: string | null;
  onPushEndpointChangeAction?: (endpoint: string | null) => void;
};

export default function NotificationSettingsView(props: NotificationSettingsViewProps) {
  const t = useTranslations('portal.notifications');
  if (isPortalBuild) {
    return (
      <a className="link link-primary" href={resolveAppHref('/home')}>
        {t('openWebSettings')}
      </a>
    );
  }
  return <WebNotificationSettingsView {...props} />;
}

function WebNotificationSettingsView({
  projectId,
  isSubscribed,
  pushEndpoint,
  onPushEndpointChangeAction,
}: NotificationSettingsViewProps) {
  const t = useTranslations('workspace.page');
  const tProfile = useTranslations('profile.page');
  const tRoot = useTranslations();
  const queryClient = useQueryClient();

  const session = useMemo(() => getAuthSession(), []);
  const token = session?.token ?? null;
  // 데스크탑 webview에서는 사전 가드(SW/PushManager/VAPID/permission)가 의도치 않게
  // 토글을 disabled로 잠그는 경우가 있어, 토큰만 있으면 클릭은 허용한다.
  // 실제 pushManager.subscribe 가능 여부는 토글 진행 시 진단한다.
  const isDesktop = useMemo(() => hasDesktopBridge(), []);

  const [warning, setWarning] = useState<string | null>(null);
  const [isTogglingOverall, setIsTogglingOverall] = useState(false);
  const [localIsSubscribed, setLocalIsSubscribed] = useState(isSubscribed);
  const [localPushEndpoint, setLocalPushEndpoint] = useState<string | null>(pushEndpoint);

  const subscribeMutation = useSubscribePushNotificationMutation(token ?? '', projectId);
  const unsubscribeMutation = useUnsubscribePushNotificationMutation(token ?? '', projectId);

  const preferencesQuery = useNotificationPreferences(token, projectId, true);
  const updatePreferencesMutation = useUpdateNotificationPreferencesMutation(token, projectId);
  const sendTestNotificationMutation = useSendTestNotificationMutation(token, projectId);

  useEffect(() => {
    setLocalIsSubscribed(isSubscribed);
  }, [isSubscribed]);

  useEffect(() => {
    setLocalPushEndpoint(pushEndpoint);
  }, [pushEndpoint]);

  useEffect(() => {
    if (!isDesktop || pushEndpoint) {
      return;
    }

    const endpoint = getDesktopPushEndpoint();
    onPushEndpointChangeAction?.(endpoint);
    setLocalPushEndpoint(endpoint);
  }, [isDesktop, onPushEndpointChangeAction, pushEndpoint]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (!token) {
      setWarning(tProfile('toastAuthRequired'));
      return;
    }

    // 데스크탑은 Web Push 전제(SW/PushManager/VAPID/permission)와 무관하게 동작한다.
    if (isDesktop) {
      setWarning(null);
      return;
    }

    if (!vapidPublicKey) {
      setWarning(tProfile('pushVapidMissing'));
      return;
    }

    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setWarning(tProfile('pushUnsupported'));
      return;
    }

    if (Notification.permission === 'denied') {
      setWarning(tProfile('pushPermissionDenied'));
      return;
    }

    setWarning(null);
  }, [token, tProfile, isDesktop]);

  const isControlsDisabled =
    Boolean(warning) || isTogglingOverall || subscribeMutation.isPending || unsubscribeMutation.isPending;

  const currentPreferences: NotificationPreference[] = useMemo(() => {
    const data = preferencesQuery.data;
    if (Array.isArray(data) && data.length > 0) {
      return data;
    }

    return allNotificationTypes.map(({ type }) => ({ type, enabled: isDefaultNotificationEnabled(type) }));
  }, [preferencesQuery.data]);

  const enabledCount = currentPreferences.filter((p) => p.enabled).length;
  const totalCount = allNotificationTypes.length;

  const handleToggleOverall = async (enabled: boolean) => {
    if (!token) {
      toast.error(tProfile('toastAuthRequired'));
      return;
    }

    if (warning) {
      toast.error(warning);
      return;
    }

    if (!localPushEndpoint && !enabled) {
      return;
    }

    setIsTogglingOverall(true);
    try {
      if (isDesktop) {
        // 데스크탑 앱은 Web Push가 동작하지 않으므로 pushManager를 우회한다.
        // SSE 채널 매칭을 위한 의사 endpoint(desktop://{deviceId})만 백엔드에 저장한다.
        const endpoint = getDesktopPushEndpoint();

        if (enabled) {
          const payload: PushSubscriptionPayload = { endpoint, kind: 'DESKTOP' };
          await subscribeMutation.mutateAsync(payload);
          onPushEndpointChangeAction?.(endpoint);
          setLocalPushEndpoint(endpoint);
          setLocalIsSubscribed(true);

          if (shouldRestoreDefaultPreferences(preferencesQuery.data)) {
            await updatePreferencesMutation.mutateAsync(
              allNotificationTypes.map(({ type }) => ({ type, enabled: isDefaultNotificationEnabled(type) })),
            );
          }

          toast.success(t('notificationEnabled'));
          await queryClient.invalidateQueries({ queryKey: queryKeys.push.preferences(projectId) });
          await queryClient.invalidateQueries({ queryKey: queryKeys.push.subscriptions(endpoint) });
        } else {
          const targetEndpoint = localPushEndpoint ?? endpoint;
          await unsubscribeMutation.mutateAsync({ endpoint: targetEndpoint });
          await updatePreferencesMutation.mutateAsync(
            allNotificationTypes.map(({ type }) => ({ type, enabled: false })),
          );
          onPushEndpointChangeAction?.(null);
          setLocalPushEndpoint(null);
          setLocalIsSubscribed(false);

          toast.success(t('notificationDisabled'));
          await queryClient.invalidateQueries({ queryKey: queryKeys.push.subscriptionsAll });
          await queryClient.invalidateQueries({ queryKey: queryKeys.push.subscriptions(targetEndpoint) });
        }
        return;
      }

      const registration = await navigator.serviceWorker.ready;
      const current = await registration.pushManager.getSubscription();

      if (enabled) {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          toast.error(tProfile('pushPermissionDenied'));
          return;
        }

        const next =
          current ??
          (await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: base64UrlToUint8Array(vapidPublicKey),
          }));

        const json = next.toJSON();
        if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
          throw new Error(tProfile('pushInvalidSubscription'));
        }

        const payload: PushSubscriptionPayload = {
          endpoint: json.endpoint,
          p256dh: json.keys.p256dh,
          auth: json.keys.auth,
        };

        await subscribeMutation.mutateAsync(payload);
        onPushEndpointChangeAction?.(payload.endpoint);
        setLocalPushEndpoint(payload.endpoint);
        setLocalIsSubscribed(true);

        if (shouldRestoreDefaultPreferences(preferencesQuery.data)) {
          await updatePreferencesMutation.mutateAsync(
            allNotificationTypes.map(({ type }) => ({ type, enabled: isDefaultNotificationEnabled(type) })),
          );
        }

        toast.success(t('notificationEnabled'));
        await queryClient.invalidateQueries({ queryKey: queryKeys.push.preferences(projectId) });
        await queryClient.invalidateQueries({ queryKey: queryKeys.push.subscriptions(payload.endpoint) });
        return;
      }

      if (current) {
        await unsubscribeMutation.mutateAsync({ endpoint: current.endpoint });
        await updatePreferencesMutation.mutateAsync(allNotificationTypes.map(({ type }) => ({ type, enabled: false })));
        onPushEndpointChangeAction?.(null);
        setLocalPushEndpoint(null);
        setLocalIsSubscribed(false);

        toast.success(t('notificationDisabled'));
        await queryClient.invalidateQueries({ queryKey: queryKeys.push.subscriptionsAll });
        await queryClient.invalidateQueries({ queryKey: queryKeys.push.subscriptions(current.endpoint) });
      }
    } catch (error) {
      const message = resolveApiErrorToastMessageOrFallback(tRoot, error, t('notificationUpdateFailed'));
      if (message) toast.error(message);
    } finally {
      setIsTogglingOverall(false);
    }
  };

  const handleTogglePreference = async (type: NotificationTypeKey, enabled: boolean) => {
    const next = currentPreferences.map((item) => (item.type === type ? { ...item, enabled } : item));
    try {
      await updatePreferencesMutation.mutateAsync(next);
    } catch (error) {
      const message = resolveApiErrorToastMessageOrFallback(tRoot, error, t('notificationUpdateFailed'));
      if (message) toast.error(message);
    }
  };

  const handleToggleGroup = async (group: NotificationGroup, enabled: boolean) => {
    const groupTypeSet = new Set(group.types.map((g) => g.type));
    const next = currentPreferences.map((item) => (groupTypeSet.has(item.type) ? { ...item, enabled } : item));
    try {
      await updatePreferencesMutation.mutateAsync(next);
    } catch (error) {
      const message = resolveApiErrorToastMessageOrFallback(tRoot, error, t('notificationUpdateFailed'));
      if (message) toast.error(message);
    }
  };

  const handleSendTestNotification = async () => {
    if (!token) {
      toast.error(tProfile('toastAuthRequired'));
      return;
    }

    // 누른 기기의 푸시 경로만 검증되도록 현재 기기 endpoint로 한정한다.
    // 데스크탑 앱은 desktop:// 의사 endpoint(구독 시와 동일한 deviceId)를 재계산하고,
    // 일반 브라우저는 구독된 웹 푸시 endpoint를 사용한다. 둘 다 없으면 멤버 전체로 폴백한다.
    const testEndpoint = isDesktop ? getDesktopPushEndpoint() : localPushEndpoint;

    try {
      const result = await sendTestNotificationMutation.mutateAsync(testEndpoint);
      if (result.delivered > 0) {
        toast.success(t('notificationTestSent'));
      } else {
        toast.error(t('notificationTestNoSubscription'));
      }
    } catch (error) {
      const message = resolveApiErrorToastMessageOrFallback(tRoot, error, t('notificationTestFailed'));
      if (message) toast.error(message);
    }
  };

  const isPermissionBlocked = Boolean(warning);
  const isListDisabled = !localIsSubscribed || isControlsDisabled;

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      {isPermissionBlocked ? (
        <div className="rounded-xl border border-warning/30 bg-warning/10 p-4">
          <div className="flex items-start gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-warning/15 text-warning">
              <AlertTriangle className="h-5 w-5" aria-hidden="true" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-base-content">{t('notificationPermissionBlockedTitle')}</p>
              <p className="mt-1 text-xs text-[var(--color-fg-muted)]">{warning}</p>
              {isIosDevice() ? (
                <p className="mt-1 text-xs text-[var(--color-fg-muted)]">{tProfile('pushIosHint')}</p>
              ) : null}
              {isAndroidDevice() ? (
                <p className="mt-1 text-xs text-[var(--color-fg-muted)]">{tProfile('pushAndroidHint')}</p>
              ) : null}
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-base-300 bg-base-200 p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-base-content">{t('notificationOverall')}</p>
              <p className="mt-0.5 text-xs text-[var(--color-fg-muted)]">{t('notificationOverallDescription')}</p>
              {localIsSubscribed ? (
                <p className="mt-1.5 text-[11px] font-medium text-[var(--color-fg-subtle)] tabular-nums">
                  {t('notificationOverallSummary', { count: enabledCount, total: totalCount })}
                </p>
              ) : null}
            </div>
            <input
              type="checkbox"
              className="toggle toggle-primary"
              checked={localIsSubscribed}
              disabled={isControlsDisabled}
              onChange={(event) => {
                void handleToggleOverall(event.target.checked);
              }}
            />
          </div>
          {localIsSubscribed ? (
            <button
              type="button"
              className="btn btn-outline btn-sm mt-3 w-full"
              disabled={isControlsDisabled || sendTestNotificationMutation.isPending}
              onClick={() => {
                void handleSendTestNotification();
              }}
            >
              {sendTestNotificationMutation.isPending ? t('notificationTestSending') : t('notificationTestSend')}
            </button>
          ) : null}
        </div>
      )}

      <div className={`rounded-xl border border-base-300 bg-base-100 ${isListDisabled ? 'opacity-50' : ''}`}>
        <div className="flex items-center justify-between border-b border-base-200 px-4 py-3">
          <p className="text-sm font-semibold text-base-content">{t('notificationTypesTitle')}</p>
          {preferencesQuery.isFetching ? <Spinner /> : null}
        </div>

        <div className="divide-y divide-base-200">
          {notificationGroups.map((group) => {
            const Icon = group.icon;
            const groupTypeSet = new Set(group.types.map((g) => g.type));
            const groupPrefs = currentPreferences.filter((p) => groupTypeSet.has(p.type));
            const groupEnabledCount = groupPrefs.filter((p) => p.enabled).length;
            const allEnabled = groupEnabledCount === group.types.length;

            return (
              <div key={group.id} className="px-4 py-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Icon className="h-4 w-4 shrink-0 text-[var(--color-fg-muted)]" />
                    <p className="text-sm font-semibold text-base-content">{t(group.labelKey)}</p>
                    <span className="text-[11px] font-medium text-[var(--color-fg-subtle)] tabular-nums">
                      {groupEnabledCount}/{group.types.length}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="text-xs font-medium text-primary transition hover:text-primary/80 disabled:cursor-not-allowed disabled:text-[var(--color-fg-disabled)]"
                    disabled={isListDisabled}
                    onClick={() => {
                      void handleToggleGroup(group, !allEnabled);
                    }}
                  >
                    {allEnabled ? t('notificationGroupDisableAll') : t('notificationGroupEnableAll')}
                  </button>
                </div>

                <div className="mt-2 space-y-1">
                  {group.types.map((item) => {
                    const checked = currentPreferences.find((pref) => pref.type === item.type)?.enabled ?? true;
                    return (
                      <label
                        key={item.type}
                        className={`flex cursor-pointer items-center justify-between gap-3 rounded-md px-2 py-1.5 ${
                          isListDisabled ? '' : 'hover:bg-base-200'
                        }`}
                      >
                        <span className="text-sm text-base-content/80">{t(item.labelKey)}</span>
                        <input
                          type="checkbox"
                          className="checkbox checkbox-sm checkbox-primary"
                          checked={checked}
                          disabled={isListDisabled}
                          onChange={(event) => {
                            void handleTogglePreference(item.type, event.target.checked);
                          }}
                        />
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 업무 시간·개인 알림 규칙은 계정 전역이라 프로필 설정으로 옮겼다. "왜 푸시가 안 오지"의 답을 잃지 않도록 위치만 안내한다. */}
      <p className="text-xs text-[var(--color-fg-muted)]">{t('notificationPersonalRulesMoved')}</p>

      {preferencesQuery.isError ? (
        <p className="text-xs text-warning">{t('notificationPreferencesLoadFailed')}</p>
      ) : null}
    </div>
  );
}
